import { Alert, Button, Stack, Typography } from '@mui/material';
import { useState, type FormEvent } from 'react';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';

import * as adminEndpoints from '../api/admin/endpoints';
import * as businessEndpoints from '../api/business/endpoints';
import { audienceOfKind, kindOfAudience } from '../components/audience';
import { PublicPage } from '../components/PublicPage';
import { publicErrorMessage } from '../components/publicErrorMessage';
import { RevealablePasswordField } from '../components/RevealablePasswordField';
import { useT } from '../i18n/LanguageContext';

/**
 * Where a reset or invitation mail leads: `/password/set?token=…&audience=business|admin&purpose=…`. The
 * audience decides which API spends the token; the API decides whether it is still good.
 */
export const SetPasswordPage = () => {
  const t = useT();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') ?? '';
  const kind = kindOfAudience(searchParams.get('audience'));
  const isInvitation = searchParams.get('purpose') === 'invitation';
  const [password, setPassword] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isBusy, setIsBusy] = useState(false);
  const [isDone, setIsDone] = useState(false);
  const loginLink = `/login?audience=${audienceOfKind(kind)}`;

  const submit = async (event: FormEvent): Promise<void> => {
    event.preventDefault();

    if (password !== confirmation) {
      setError(t('setPassword.mismatch'));

      return;
    }

    setError(null);
    setIsBusy(true);

    try {
      await (kind === 'platform'
        ? adminEndpoints.setPasswordFromLink(token, password)
        : businessEndpoints.setPasswordFromLink(token, password));
      setIsDone(true);
    } catch (caught) {
      setError(publicErrorMessage(caught, t));
    } finally {
      setIsBusy(false);
    }
  };

  return (
    <PublicPage>
      <Typography variant="h6" sx={{ mb: 1.5 }}>
        {isInvitation ? t('setPassword.title.invitation') : t('setPassword.title.reset')}
      </Typography>
      {token === '' ? (
        <Stack spacing={2}>
          <Alert severity="warning" variant="outlined">
            {t('setPassword.missingToken')}
          </Alert>
          <Button
            component={RouterLink}
            to={`/password/forgot?audience=${audienceOfKind(kind)}`}
            variant="outlined"
          >
            {t('setPassword.requestNew')}
          </Button>
        </Stack>
      ) : isDone ? (
        <Stack spacing={2}>
          <Alert severity="success" variant="outlined">
            {t('setPassword.done')}
          </Alert>
          <Button component={RouterLink} to={loginLink} variant="contained">
            {t('setPassword.toLogin')}
          </Button>
        </Stack>
      ) : (
        <>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3, lineHeight: 1.55 }}>
            {t('setPassword.hint')}
          </Typography>
          <form onSubmit={submit}>
            <Stack spacing={2}>
              {error ? (
                <Alert severity="error" variant="outlined">
                  {error}
                </Alert>
              ) : null}
              <RevealablePasswordField
                label={t('setPassword.password')}
                value={password}
                onChange={setPassword}
                autoComplete="new-password"
                autoFocus
              />
              <RevealablePasswordField
                label={t('setPassword.confirm')}
                value={confirmation}
                onChange={setConfirmation}
                autoComplete="new-password"
              />
              <Button type="submit" variant="contained" size="large" disabled={isBusy}>
                {isBusy ? t('setPassword.busy') : t('setPassword.submit')}
              </Button>
              <Button
                component={RouterLink}
                to={`/password/forgot?audience=${audienceOfKind(kind)}`}
                size="small"
              >
                {t('setPassword.requestNew')}
              </Button>
            </Stack>
          </form>
        </>
      )}
    </PublicPage>
  );
};
